import React, { useState, useEffect } from 'react';
import { Formik, Form, Field, useFormikContext } from 'formik';
import * as Yup from 'yup';
import {
  Typography,
  TextField,
  Grid,
  Box,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Chip,
  Alert,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@mui/material';
import {
  InitialConfigFormProps,
  PartsVariables,
  LaborVariables,
} from './types';
import {
  getAvailableBrands,
  getDefaultPartsVariables,
  getDefaultLaborVariables,
  calculateAllConditionalValues,
} from './utils/conditionalLogic';
import { debounce } from './utils/autosave';
//import ClaimsTrackerClient from '@shared/clients/ClaimsTrackerClient';
import { formatCostaRicanCurrency } from './utils/currencyFormatter';

const validationSchema = Yup.object({
  discount: Yup.number()
    .min(0, 'El descuento debe ser mayor o igual a 0')
    .max(100, 'El descuento no puede ser mayor a 100%')
    .required('El descuento es requerido'),
  vat: Yup.number()
    .min(0, 'El IVA debe ser mayor o igual a 0')
    .max(100, 'El IVA no puede ser mayor a 100%')
    .required('El IVA es requerido'),
  bodyworkRate: Yup.number()
    .min(0, 'La tarifa debe ser mayor o igual a 0')
    .required('La tarifa de carrocería es requerida'),
  paintRate: Yup.number()
    .min(0, 'La tarifa debe ser mayor o igual a 0')
    .required('La tarifa de pintura es requerida'),
  mechanicalRate: Yup.number()
    .min(0, 'La tarifa debe ser mayor o igual a 0')
    .required('La tarifa de mecánica es requerida'),
  underinsurance: Yup.number()
    .min(0, 'El infraseguro debe ser mayor o igual a 0')
    .when(['isStructuralLoss', 'isFinancialLoss'], {
      is: (isStructuralLoss: boolean, isFinancialLoss: boolean) =>
        isStructuralLoss || isFinancialLoss,
      then: (schema) =>
        schema.required(
          'El infraseguro es requerido para pérdidas estructurales y financieras',
        ),
      otherwise: (schema) => schema.required('El infraseguro es requerido'),
    }),
  depreciation: Yup.number()
    .min(0, 'Las depreciaciones deben ser mayor o igual a 0')
    .required('Las depreciaciones son requeridas'),
  deductible: Yup.number()
    .min(0, 'El deducible debe ser mayor o igual a 0')
    .when('isStructuralLoss', {
      is: true,
      then: (schema) =>
        schema.required(
          'El deducible es requerido para pérdidas estructurales',
        ),
      otherwise: (schema) => schema.required('El deducible es requerido'),
    }),
  isStructuralLoss: Yup.boolean().required('El tipo de pérdida es requerido'),
});

// Component to handle automatic form updates
const FormUpdater: React.FC<{
  conditionalValues: {
    partsDiscount: number;
    bodyworkRateCost: number;
    paintRateCost: number;
    mechanicalRateCost: number;
    isValid: boolean;
    errors: string[];
  };
  isJsonLoaded?: boolean;
}> = ({ conditionalValues, isJsonLoaded = false }) => {
  const { setFieldValue } = useFormikContext<any>();

  useEffect(() => {
    // Only update fields if not loaded from JSON
    if (!isJsonLoaded) {
      // Update discount field with calculated value
      setFieldValue('discount', conditionalValues.partsDiscount);

      // Update labor rates with calculated value
      setFieldValue('bodyworkRate', conditionalValues.bodyworkRateCost);
      setFieldValue('paintRate', conditionalValues.paintRateCost);
      setFieldValue('mechanicalRate', conditionalValues.mechanicalRateCost);
    }
    // If loaded from JSON, do nothing - let the JSON values remain
  }, [
    conditionalValues.partsDiscount,
    conditionalValues.bodyworkRateCost,
    conditionalValues.paintRateCost,
    conditionalValues.mechanicalRateCost,
    setFieldValue,
    isJsonLoaded,
  ]);

  return null;
};

const InitialConfigForm: React.FC<InitialConfigFormProps> = ({
  poNumber,
  claimUId,
  onNext,
  initialValues,
  isJsonLoaded = false,
}) => {
  const [partsVariables, setPartsVariables] = useState<PartsVariables>(
    getDefaultPartsVariables(),
  );
  const [laborVariables, setLaborVariables] = useState<LaborVariables>(
    getDefaultLaborVariables(),
  );
  const [jsonIsLoaded, setJsonIsLoaded] = useState<boolean>(isJsonLoaded);
  const [hasUserInteracted, setHasUserInteracted] = useState<boolean>(false);
  const [previousFormValues, setPreviousFormValues] = useState<any>(null);
  const [conditionalValues, setConditionalValues] = useState({
    laborCost: 0,
    partsDiscount: 15,
    bodyworkRateCost: 0,
    paintRateCost: 0,
    mechanicalRateCost: 0,
    isValid: true,
    errors: [] as string[],
  });

  // Función de autosave con debounce - actualiza registro activo

  const debouncedAutosave = debounce((formData: any) => {
    if (!hasUserInteracted) return; // No autosave en carga inicial

    // Comparar con valores anteriores
    if (
      previousFormValues &&
      JSON.stringify(formData) === JSON.stringify(previousFormValues)
    ) {
      console.log('🚫 AUTOSAVE - Sin cambios detectados, omitiendo autosave');
      return;
    }

    // Crear datos de estimación para guardar/actualizar

    /*const completeJsonData = {
      poNumber: poNumber,
      claimUId: claimUId,
      timestamp: new Date().toISOString(),
      isDeleted: false,
      deletedAt: null,
      configValues: {
        ...formData,
        isStructuralLoss: formData.isStructuralLoss || false,
      },
      inputValues: formData,
      calculations: {
        netTotalParts: 0,
        preliminaryPartsLoss: 0,
        hiddenDamagePartsWithVAT: 0,
        totalPartsLoss: 0,
        preliminaryLabor: 0,
        hiddenDamageLaborWithVAT: 0,
        totalLaborLoss: 0,
        totalLossAmount: 0,
        totalRepairAmount: 0,
        initialIndemnityAmount: 0,
        finalIndemnityAmount: 0,
      },
      conditionalVariables: {
        partsVariables: partsVariables || {
          workshop: 'Purdy Auto',
          company: 'Purdy Motor',
          brand: 'Toyota',
        },
        laborVariables: laborVariables || {
          workshop: 'Purdy Auto',
          type: 'Interno',
        },
      },
      summary: {
        totalPartsLoss: 0,
        totalLaborLoss: 0,
        totalLossAmount: 0,
        finalIndemnityAmount: 0,
      },
      breakdown: {
        parts: {
          netParts: formData.netParts,
          discount: formData.discount,
          vat: formData.vat,
          netTotalParts: 0,
          preliminaryPartsLoss: 0,
          hiddenDamagePartsWithVAT: 0,
          totalPartsLoss: 0,
          conditionalDiscount: partsVariables
            ? `${partsVariables.workshop} + ${partsVariables.company} = ${formData.discount}%`
            : 'No aplicado',
        },
        labor: {
          bodyworkHours: formData.bodyworkHours,
          paintHours: formData.paintHours,
          mechanicalHours: formData.mechanicalHours,
          bodyworkRate: formData.bodyworkRate,
          paintRate: formData.paintRate,
          mechanicalRate: formData.mechanicalRate,
          preliminaryLabor: 0,
          hiddenDamageLaborWithVAT: 0,
          totalLaborLoss: 0,
          conditionalLaborCost: laborVariables
            ? `${laborVariables.workshop} + ${laborVariables.type} = ₡${formData.bodyworkRate}`
            : 'No aplicado',
        },
        adjustments: {
          underinsurance: formData.underinsurance,
          depreciation: formData.depreciation,
          deductible: formData.deductible,
        },
      },
    };*/

    // Guardar o actualizar estimación activa
    try {
      //const id = PersistentDB.saveOrUpdateActiveEstimation(estimationData);
      /*(async () => {
        await new ClaimsTrackerClient().SaveEstimatedAmount(
          {
            partsCost: completeJsonData.calculations.totalPartsLoss.toString(),
            porcDesc: completeJsonData.configValues.discount.toString(),
            discountAmount: '0',
            subtotalParts: completeJsonData.calculations.totalPartsLoss.toString(),
            partsCostWithoutDiscount: '0',
            partsCostTotal: completeJsonData.calculations.totalPartsLoss.toString(),
            expenseAmount: completeJsonData.calculations.totalLaborLoss.toString(),
            materialsCost: '0',
            laborCost: completeJsonData.calculations.totalLaborLoss.toString(),
            ivuLaborAmount: '0',
            ivuPartsAmount: '0',
            estimatedTotalAmount: completeJsonData.calculations.finalIndemnityAmount.toString(),
            porcIVA: completeJsonData.configValues.vat.toString(),
            ivaAmount: '0',
            previousDamageCost: '0',
            jsonCustomEstimateData: JSON.stringify(completeJsonData),
          },
          claimUId,
          '',
        );
      })();*/
      //console.log('🔄 AUTOSAVE - Configuración Inicial actualizada:', id);
      console.log('📊 Data actualizada en registro activo');
      console.log('✅ Configuración guardada automáticamente');
      console.log('---');

      // Guardar valores actuales para próxima comparación
      setPreviousFormValues(formData);
    } catch (error) {
      console.error('❌ Error en autosave:', error);
    }
  }, 1500);

  // Update conditional values when variables change
  useEffect(() => {
    // Check if JSON was loaded using localStorage
    const isJsonLoaded =
      localStorage.getItem('estimation_json_loaded') === 'true';
    const jsonValues = localStorage.getItem('estimation_json_values');

    if (isJsonLoaded && jsonValues) {
      // When loaded from JSON, use JSON values and invalidate labor cost calculation
      const parsedJsonValues = JSON.parse(jsonValues);

      // Set conditional variables from JSON
      const partsVariablesByJson = parsedJsonValues.partsVariables as PartsVariables;
      const laborVariablesByJson = parsedJsonValues.laborVariables as LaborVariables;

      setPartsVariables(partsVariablesByJson);
      setLaborVariables(laborVariablesByJson);

      // Calculate conditional values with JSON values
      const calculated = calculateAllConditionalValues(
        partsVariables,
        laborVariables,
      );

      // Override labor cost with JSON values to prevent matrix interference
      const conditionalValuesWithJson = {
        ...calculated,
        laborCost: calculated.laborCost,
        bodyworkRateCost: parsedJsonValues.bodyworkRate,
        paintRateCost: parsedJsonValues.paintRate,
        mechanicalRateCost: parsedJsonValues.mechanicalRate,
        partsDiscount: parsedJsonValues.discount as number,
        isValid: true,
        errors: [],
      };

      console.log('Using JSON values, labor cost invalidated:', {
        jsonValues: parsedJsonValues,
        conditionalValues: conditionalValuesWithJson,
      });

      setTimeout(() => {
        setConditionalValues(conditionalValuesWithJson);
      }, 2000);

      // Clear the localStorage flag after use
      console.log(
        'estimation_json_values',
        localStorage.getItem('estimation_json_values'),
      );
      console.log('Clearing localStorage flags');
      localStorage.removeItem('estimation_json_loaded');
      localStorage.removeItem('estimation_json_values');
      setJsonIsLoaded(false);
    } else {
      // Normal calculation when not loaded from JSON
      const calculated = calculateAllConditionalValues(
        partsVariables,
        laborVariables,
      );
      console.log('Normal calculation:', calculated);
      setConditionalValues(calculated);
    }
  }, [partsVariables, laborVariables]);

  // Autosave when conditional variables change - JSON completo
  useEffect(() => {
    if (!jsonIsLoaded && hasUserInteracted) {
      // Crear estructura JSON completa según el formato estándar
      const completeJsonData = {
        poNumber: poNumber,
        claimUId: claimUId,
        timestamp: new Date().toISOString(),
        isDeleted: false,
        deletedAt: null,
        configValues: {
          discount: 15, // Valores por defecto, en un caso real se obtendrían del estado
          vat: 13,
          bodyworkRate: 0,
          paintRate: 0,
          mechanicalRate: 0,
          underinsurance: 0,
          depreciation: 0,
          deductible: 0,
          isStructuralLoss: false,
          isFinancialLoss: false,
        },
        inputValues: {
          netParts: 0,
          bodyworkHours: 0,
          paintHours: 0,
          mechanicalHours: 0,
          hiddenDamagePartsNoVAT: 0,
          hiddenDamageLaborNoVAT: 0,
          additionalLabor: 0,
          additionalParts: 0,
          subletAmount: 0,
          bodyworkUT: 0,
          paintUT: 0,
          mechanicalUT: 0,
        },
        calculations: {
          netTotalParts: 0,
          preliminaryPartsLoss: 0,
          hiddenDamagePartsWithVAT: 0,
          totalPartsLoss: 0,
          preliminaryLabor: 0,
          hiddenDamageLaborWithVAT: 0,
          totalLaborLoss: 0,
          totalLossAmount: 0,
          totalRepairAmount: 0,
          initialIndemnityAmount: 0,
          finalIndemnityAmount: 0,
        },
        conditionalVariables: {
          partsVariables,
          laborVariables,
        },
        summary: {
          totalPartsLoss: 0,
          totalLaborLoss: 0,
          totalLossAmount: 0,
          finalIndemnityAmount: 0,
        },
        breakdown: {
          parts: {
            netParts: 0,
            discount: 15,
            vat: 13,
            netTotalParts: 0,
            preliminaryPartsLoss: 0,
            hiddenDamagePartsWithVAT: 0,
            totalPartsLoss: 0,
            conditionalDiscount: partsVariables
              ? `${partsVariables.workshop} + ${
                  partsVariables.company
                } = ${15}%`
              : 'No aplicado',
          },
          labor: {
            bodyworkHours: 0,
            paintHours: 0,
            mechanicalHours: 0,
            bodyworkRate: 0,
            paintRate: 0,
            mechanicalRate: 0,
            preliminaryLabor: 0,
            hiddenDamageLaborWithVAT: 0,
            totalLaborLoss: 0,
            conditionalLaborCost: laborVariables
              ? `${laborVariables.workshop} + ${laborVariables.type} = ₡0`
              : 'No aplicado',
          },
          adjustments: {
            underinsurance: 0,
            depreciation: 0,
            deductible: 0,
          },
        },
      };

      // Crear datos de estimación para guardar/actualizar (formato interno)
      //const estimationData = {
      //  configValues: completeJsonData.configValues,
      //  inputValues: completeJsonData.inputValues,
      //  conditionalVariables: completeJsonData.conditionalVariables,
      //  calculations: completeJsonData.calculations,
      //};

      // Guardar o actualizar estimación activa
      try {
        //const id = PersistentDB.saveOrUpdateActiveEstimation(estimationData);
        //console.log('🔄 AUTOSAVE - Variables Condicionales actualizadas:', id);
        console.log(
          '📊 JSON completo generado:',
          JSON.stringify(completeJsonData, null, 2),
        );
        console.log('✅ Variables condicionales guardadas automáticamente');
        console.log('---');
      } catch (error) {
        console.error(
          '❌ Error en autosave de variables condicionales:',
          error,
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    partsVariables,
    laborVariables,
    jsonIsLoaded,
    hasUserInteracted,
    conditionalValues,
  ]);

  const handleSubmit = (values: any): void => {
    // Include conditional variables in the submission
    const configWithVariables = {
      ...values,
      partsVariables,
      laborVariables,
      conditionalValues,
    };
    onNext(configWithVariables);
  };

  // Auto-update when form values change
  const handleFormChange = (values: any): void => {
    const configWithVariables = {
      ...values,
      partsVariables,
      laborVariables,
      conditionalValues,
    };

    onNext(configWithVariables);
  };

  // Función para manejar el blur de los campos
  const handleFieldBlur = (values: any): void => {
    // Marcar que el usuario ha interactuado
    setHasUserInteracted(true);

    // Trigger autosave solo en blur
    debouncedAutosave(values);
    console.log('InitialConfigForm values changed:', values);
  };

  return (
    <Box>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Configure los valores base para los cálculos de pérdidas y reparaciones
      </Typography>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ errors, touched, values }) => {
          // Auto-update when values change
          // eslint-disable-next-line react-hooks/rules-of-hooks
          useEffect(() => {
            handleFormChange(values);
          }, [values]);

          // Función para manejar blur de campos
          const handleFormBlur = () => {
            handleFieldBlur(values);
          };

          return (
            <Form>
              {!isJsonLoaded && (
                <FormUpdater
                  conditionalValues={conditionalValues}
                  isJsonLoaded={isJsonLoaded}
                />
              )}
              <Grid container spacing={3}>
                {/* Variables Condicionales */}
                <Grid item xs={12}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Variables Condicionales
                      </Typography>

                      {conditionalValues.errors.length > 0 && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                          {conditionalValues.errors.map((error, index) => (
                            <div key={index}>{error}</div>
                          ))}
                        </Alert>
                      )}

                      <Grid container spacing={2}>
                        {/* Variables Repuestos */}
                        <Grid item xs={12} md={6}>
                          <Typography variant="subtitle1" gutterBottom>
                            Variables Repuestos
                          </Typography>

                          <Grid container spacing={2}>
                            <Grid item xs={12}>
                              <FormControl fullWidth>
                                <InputLabel>Taller</InputLabel>
                                <Select
                                  value={partsVariables.workshop}
                                  onChange={(e) => {
                                    const newWorkshop = e.target.value as
                                      | 'Purdy Auto'
                                      | 'Otros Talleres';
                                    if (!jsonIsLoaded) {
                                      setHasUserInteracted(true);
                                      setPartsVariables((prev) => ({
                                        ...prev,
                                        workshop: newWorkshop,
                                      }));
                                    }
                                  }}
                                >
                                  <MenuItem value="Purdy Auto">
                                    Purdy Auto
                                  </MenuItem>
                                  <MenuItem value="Otros Talleres">
                                    Otros Talleres
                                  </MenuItem>
                                </Select>
                              </FormControl>
                            </Grid>

                            <Grid item xs={12}>
                              <FormControl fullWidth>
                                <InputLabel>Empresa</InputLabel>
                                <Select
                                  value={partsVariables.company}
                                  onChange={(e) => {
                                    const newCompany = e.target.value as
                                      | 'Purdy Motor'
                                      | 'Automotriz';
                                    const availableBrands = getAvailableBrands(
                                      newCompany,
                                    );
                                    if (!isJsonLoaded) {
                                      setHasUserInteracted(true);
                                      setPartsVariables((prev) => ({
                                        ...prev,
                                        company: newCompany,
                                        brand: availableBrands[0],
                                      }));
                                    }
                                  }}
                                >
                                  <MenuItem value="Purdy Motor">
                                    Purdy Motor
                                  </MenuItem>
                                  <MenuItem value="Automotriz">
                                    Automotriz
                                  </MenuItem>
                                </Select>
                              </FormControl>
                            </Grid>

                            <Grid item xs={12}>
                              <FormControl fullWidth>
                                <InputLabel>Marca</InputLabel>
                                <Select
                                  value={partsVariables.brand}
                                  onChange={(e) => {
                                    const newBrand = e.target.value;
                                    if (!isJsonLoaded) {
                                      setHasUserInteracted(true);
                                      setPartsVariables((prev) => ({
                                        ...prev,
                                        brand: newBrand,
                                      }));
                                    }
                                  }}
                                >
                                  {getAvailableBrands(
                                    partsVariables.company,
                                  ).map((brand) => (
                                    <MenuItem key={brand} value={brand}>
                                      {brand}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Grid>
                          </Grid>
                        </Grid>

                        {/* Variables Mano de Obra */}
                        <Grid item xs={12} md={6}>
                          <Typography variant="subtitle1" gutterBottom>
                            Variables Mano de Obra
                          </Typography>

                          <Grid container spacing={2}>
                            <Grid item xs={12}>
                              <FormControl fullWidth>
                                <InputLabel>Taller</InputLabel>
                                <Select
                                  value={laborVariables.workshop}
                                  onChange={(e) => {
                                    const newWorkshop = e.target.value as
                                      | 'Purdy Auto'
                                      | 'Otros Talleres';
                                    const newType =
                                      newWorkshop === 'Purdy Auto'
                                        ? 'Interno'
                                        : 'Externo';
                                    if (!jsonIsLoaded) {
                                      setHasUserInteracted(true);
                                      setLaborVariables((prev) => ({
                                        ...prev,
                                        workshop: newWorkshop,
                                        type: newType,
                                      }));
                                    }
                                  }}
                                >
                                  <MenuItem value="Purdy Auto">
                                    Purdy Auto
                                  </MenuItem>
                                  <MenuItem value="Otros Talleres">
                                    Otros Talleres
                                  </MenuItem>
                                </Select>
                              </FormControl>
                            </Grid>

                            <Grid item xs={12}>
                              <FormControl fullWidth>
                                <InputLabel>Tipo</InputLabel>
                                <Select
                                  value={laborVariables.type}
                                  onChange={(e) => {
                                    const newType = e.target.value as
                                      | 'Interno'
                                      | 'Externo';
                                    if (!jsonIsLoaded) {
                                      setHasUserInteracted(true);
                                      setLaborVariables((prev) => ({
                                        ...prev,
                                        type: newType,
                                      }));
                                    }
                                  }}
                                >
                                  <MenuItem value="Interno">Interno</MenuItem>
                                  <MenuItem value="Externo">Externo</MenuItem>
                                </Select>
                              </FormControl>
                            </Grid>
                          </Grid>
                        </Grid>
                      </Grid>

                      {/* Mostrar valores calculados */}
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          Valores Calculados:
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          <Chip
                            label={`Descuento: ${conditionalValues.partsDiscount}%`}
                            color="primary"
                            variant="outlined"
                          />
                          <Chip
                            label={`Costo MO: ${formatCostaRicanCurrency(
                              conditionalValues.laborCost || 0,
                            )}`}
                            color="secondary"
                            variant="outlined"
                          />
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                <Divider sx={{ my: 2 }} />

                {/* Configuración de Descuentos e Impuestos */}
                <Grid item xs={12}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Configuración de Descuentos e Impuestos
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <Field name="discount">
                            {({ field }: any) => (
                              <TextField
                                {...field}
                                label="Descuento (%)"
                                type="number"
                                fullWidth
                                error={
                                  touched.discount && Boolean(errors.discount)
                                }
                                helperText={touched.discount && errors.discount}
                                inputProps={{ min: 0, max: 100, step: 0.01 }}
                                onBlur={handleFormBlur}
                              />
                            )}
                          </Field>
                        </Grid>
                        <Grid item xs={6}>
                          <Field name="vat">
                            {({ field }: any) => (
                              <TextField
                                {...field}
                                label="IVA (%)"
                                type="number"
                                fullWidth
                                error={touched.vat && Boolean(errors.vat)}
                                helperText={touched.vat && errors.vat}
                                inputProps={{ min: 0, max: 100, step: 0.01 }}
                                onBlur={handleFormBlur}
                              />
                            )}
                          </Field>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Tarifas por Tipo de Trabajo */}
                <Grid item xs={12}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Tarifas por Tipo de Trabajo
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={4}>
                          <Field name="bodyworkRate">
                            {({ field }: any) => (
                              <TextField
                                {...field}
                                label="Tarifa Carrocería (₡/hora)"
                                type="number"
                                fullWidth
                                error={
                                  touched.bodyworkRate &&
                                  Boolean(errors.bodyworkRate)
                                }
                                helperText={
                                  touched.bodyworkRate && errors.bodyworkRate
                                }
                                inputProps={{ min: 0, step: 0.01 }}
                                onBlur={handleFormBlur}
                              />
                            )}
                          </Field>
                        </Grid>
                        <Grid item xs={4}>
                          <Field name="paintRate">
                            {({ field }: any) => (
                              <TextField
                                {...field}
                                label="Tarifa Pintura (₡/hora)"
                                type="number"
                                fullWidth
                                error={
                                  touched.paintRate && Boolean(errors.paintRate)
                                }
                                helperText={
                                  touched.paintRate && errors.paintRate
                                }
                                inputProps={{ min: 0, step: 0.01 }}
                                onBlur={handleFormBlur}
                              />
                            )}
                          </Field>
                        </Grid>
                        <Grid item xs={4}>
                          <Field name="mechanicalRate">
                            {({ field }: any) => (
                              <TextField
                                {...field}
                                label="Tarifa Mecánica (₡/hora)"
                                type="number"
                                fullWidth
                                error={
                                  touched.mechanicalRate &&
                                  Boolean(errors.mechanicalRate)
                                }
                                helperText={
                                  touched.mechanicalRate &&
                                  errors.mechanicalRate
                                }
                                inputProps={{ min: 0, step: 0.01 }}
                                onBlur={handleFormBlur}
                              />
                            )}
                          </Field>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Ajustes y Cargos */}
                <Grid item xs={12}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Ajustes y Cargos
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={4}>
                          <Field name="underinsurance">
                            {({ field }: any) => (
                              <TextField
                                {...field}
                                label="Infraseguro (₡)"
                                type="number"
                                fullWidth
                                error={
                                  touched.underinsurance &&
                                  Boolean(errors.underinsurance)
                                }
                                helperText={
                                  touched.underinsurance &&
                                  errors.underinsurance
                                }
                                inputProps={{ min: 0, step: 0.01 }}
                                onBlur={handleFormBlur}
                              />
                            )}
                          </Field>
                        </Grid>
                        <Grid item xs={4}>
                          <Field name="depreciation">
                            {({ field }: any) => (
                              <TextField
                                {...field}
                                label="Depreciaciones (₡)"
                                type="number"
                                fullWidth
                                error={
                                  touched.depreciation &&
                                  Boolean(errors.depreciation)
                                }
                                helperText={
                                  touched.depreciation && errors.depreciation
                                }
                                inputProps={{ min: 0, step: 0.01 }}
                                onBlur={handleFormBlur}
                              />
                            )}
                          </Field>
                        </Grid>
                        <Grid item xs={4}>
                          <Field name="deductible">
                            {({ field }: any) => (
                              <TextField
                                {...field}
                                label="Deducible (₡)"
                                type="number"
                                fullWidth
                                error={
                                  touched.deductible &&
                                  Boolean(errors.deductible)
                                }
                                helperText={
                                  touched.deductible && errors.deductible
                                }
                                inputProps={{ min: 0, step: 0.01 }}
                                onBlur={handleFormBlur}
                              />
                            )}
                          </Field>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Sección Tipo de Pérdida */}
                <Grid item xs={12}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom color="primary">
                        Tipo de Pérdida
                      </Typography>
                      <Grid container spacing={3}>
                        {/* Pérdida Estructural */}
                        <Grid item xs={12} md={6}>
                          <FormControl component="fieldset">
                            <FormLabel component="legend">
                              ¿Es una pérdida estructural?
                            </FormLabel>
                            <Field name="isStructuralLoss">
                              {({ field, form }: any) => (
                                <RadioGroup
                                  row
                                  {...field}
                                  value={field.value}
                                  onChange={(e) => {
                                    const isStructural =
                                      e.target.value === 'true';
                                    form.setFieldValue(
                                      'isStructuralLoss',
                                      isStructural,
                                    );
                                    // Limpiar campos de indemnización cuando se selecciona pérdida estructural
                                    if (isStructural) {
                                      form.setFieldValue('underinsurance', 0);
                                      form.setFieldValue('deductible', 0);
                                    }
                                  }}
                                >
                                  <FormControlLabel
                                    value={false}
                                    control={<Radio />}
                                    label="NO"
                                  />
                                  <FormControlLabel
                                    value={true}
                                    control={<Radio />}
                                    label="SÍ"
                                  />
                                </RadioGroup>
                              )}
                            </Field>
                            {values.isStructuralLoss && (
                              <Alert severity="info" sx={{ mt: 2 }}>
                                <strong>Pérdida Estructural Detectada:</strong>{' '}
                                Se ha marcado como pérdida estructural. Los
                                campos de indemnización son requeridos.
                              </Alert>
                            )}
                          </FormControl>
                        </Grid>

                        {/* Pérdida Financiera */}
                        <Grid item xs={12} md={6}>
                          <FormControl component="fieldset">
                            <FormLabel component="legend">
                              ¿Es una pérdida financiera?
                            </FormLabel>
                            <Field name="isFinancialLoss">
                              {({ field, form }: any) => (
                                <RadioGroup
                                  row
                                  {...field}
                                  value={field.value}
                                  onChange={(e) => {
                                    const isFinancial =
                                      e.target.value === 'true';
                                    form.setFieldValue(
                                      'isFinancialLoss',
                                      isFinancial,
                                    );
                                    // Limpiar infraseguro cuando se selecciona pérdida financiera para forzar validación
                                    if (isFinancial) {
                                      form.setFieldValue('underinsurance', 0);
                                    }
                                  }}
                                >
                                  <FormControlLabel
                                    value={false}
                                    control={<Radio />}
                                    label="NO"
                                  />
                                  <FormControlLabel
                                    value={true}
                                    control={<Radio />}
                                    label="SÍ"
                                  />
                                </RadioGroup>
                              )}
                            </Field>
                            {values.isFinancialLoss && (
                              <Alert severity="warning" sx={{ mt: 2 }}>
                                <strong>Pérdida Financiera Detectada:</strong>{' '}
                                Se ha marcado como pérdida financiera. Se
                                aplicará validación de pérdida total con umbral
                                del 75% del valor asegurado.
                              </Alert>
                            )}
                          </FormControl>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Form>
          );
        }}
      </Formik>
    </Box>
  );
};

export default InitialConfigForm;
